/**
 * @file page-publish-items.ts
 * @description Пункты страниц и мест в окне «Опубликовать» (phase19.16 Р3а;
 * SB105, SB126, SB128; uiux-r3 §3.2): подписи «Страница «О нас» · 3 правки ·
 * вы», «Акции — скрыта. Показать на сайте», отметки по умолчанию и тело
 * пачки `POST pages/publish`.
 *
 * Чистые функции над `SitePageChangesDto[]` — их проверяют тесты, окно лишь
 * рисует. Пункт без правок, но скрытый с версией на сайте, — только «показать»
 * (`show: true`, версия не создаётся); у скрытого пункта с правками
 * «показывать на сайте» — отдельная галочка, по умолчанию снята (SB105).
 */

import { SITE_SLOTS } from "@chaiberi/shared";

import type { PublishPageItemInput, SitePageCheck, SitePageChangesDto } from "../api-site-page-publish";
import { draftEditCount, editorLabel, editsWord } from "./page-status";
import { pageItemDefaultChecked } from "./publish-rules";

/** Пункт окна. */
export interface PublishPageRow {
  pageId: string;
  /** «Страница «О нас»», «Главная», место — его название. */
  title: string;
  /** Подпись после названия: «3 правки · вы», «скрыта», «ещё не была на сайте». */
  meta: string;
  /** Скрыта с сайта (у пункта — галочка «показывать на сайте»). */
  hidden: boolean;
  /** Правок нет — пункт только показывает скрытую страницу. */
  showOnly: boolean;
  fingerprint: string;
  checks: SitePageCheck[];
  source: SitePageChangesDto;
  /** Р3в (SB131): адрес на сайте сменится при публикации — «/about → /about-us». */
  slugChange: { from: string; to: string } | null;
}

/** Отметки пункта: публиковать, показывать на сайте, переадресовать со старого адреса. */
export interface RowSelection {
  checked: boolean;
  show: boolean;
  /** Р3в: галочка «старый будет переадресован»; нет — да (умолчание SB131). */
  redirect?: boolean;
}

/** Сменится ли адрес на сайте: у страницы есть живая версия и другой адрес в черновике. */
export function slugChangeOf(c: Pick<SitePageChangesDto, "kind" | "slug" | "liveSlug" | "live" | "isPublished">): { from: string; to: string } | null {
  // Скрытая страница не живая — переадресации со старого адреса не будет (SB131).
  if (c.kind !== "CUSTOM" || !c.isPublished || c.live === null || !c.liveSlug || c.liveSlug === c.slug) return null;
  return { from: `/${c.liveSlug}`, to: `/${c.slug}` };
}

/** Название пункта: место — по реестру мест, главная — «Главная», иначе «Страница «…»». */
export function publishRowTitle(c: Pick<SitePageChangesDto, "kind" | "slot" | "title">): string {
  if (c.kind === "SLOT") return SITE_SLOTS.find((s) => s.key === c.slot)?.label ?? c.title;
  if (c.kind === "SYSTEM_HOME") return `Главная «${c.title}»`;
  return `Страница «${c.title}»`;
}

/** Пункты окна по ответу `GET pages/changes` (и открытой странице, если её там нет). */
export function publishRowsOf(list: readonly SitePageChangesDto[], currentUserId: string | null): PublishPageRow[] {
  return list.map((c) => {
    const hidden = !c.isPublished;
    const showOnly = !c.hasChanges;
    const who = editorLabel(c.draft.editedBy, currentUserId);
    let meta: string;
    if (c.live === null) meta = "ещё не была на сайте";
    else if (showOnly) meta = "скрыта. Показать на сайте";
    else meta = [editsWord(Math.max(1, draftEditCount(c))), who, hidden ? "скрыта" : null].filter(Boolean).join(" · ");
    return { pageId: c.pageId, title: publishRowTitle(c), meta, hidden, showOnly, fingerprint: c.fingerprint, checks: c.checks, source: c, slugChange: slugChangeOf(c) };
  });
}

/** Отметки по умолчанию (SB105): открытая видимая страница с правками; остальное — нет. */
export function defaultRowSelection(row: PublishPageRow, openPageId: string | null): RowSelection {
  return { checked: pageItemDefaultChecked(row.source, openPageId), show: false };
}

/** Тело пачки: отмеченные пункты; у скрытых — `show` по галочке, у «только показать» — всегда. */
export function publishItemsOf(rows: readonly PublishPageRow[], selection: Readonly<Record<string, RowSelection>>): PublishPageItemInput[] {
  return rows
    .filter((r) => selection[r.pageId]?.checked)
    .map((r) => {
      const show = r.showOnly || (r.hidden && selection[r.pageId]?.show === true);
      const redirect = r.slugChange ? { redirectFromOldSlug: selection[r.pageId]?.redirect !== false } : {};
      return { pageId: r.pageId, fingerprint: r.fingerprint, ...(show ? { show: true } : {}), ...redirect };
    });
}

/** Ошибки проверок отмеченных пунктов (SB127: стоп-лист). */
export function checkedRowErrors(rows: readonly PublishPageRow[], selection: Readonly<Record<string, RowSelection>>): SitePageCheck[] {
  return rows.filter((r) => selection[r.pageId]?.checked).flatMap((r) => r.checks);
}

/**
 * Новые пункты после перечитывания: отметки человека у оставшихся пунктов
 * сохраняются, новые — по умолчанию.
 */
export function mergeRowSelection(
  rows: readonly PublishPageRow[],
  prev: Readonly<Record<string, RowSelection>>,
  openPageId: string | null,
): Record<string, RowSelection> {
  const next: Record<string, RowSelection> = {};
  for (const r of rows) next[r.pageId] = prev[r.pageId] ?? defaultRowSelection(r, openPageId);
  return next;
}
